// The order an agent wants their queue in, remembered between visits.
//
// localStorage rather than the server, which is the pattern this app already uses for
// the other per-person UI preference (web/src/theme/storage.js) and for the new-ticket
// draft. It is per-browser rather than per-account: the same trade the theme makes, and
// the same one that keeps this from needing a migration, an endpoint and a round trip on
// every queue load.
//
// Every access is wrapped. localStorage throws in a private window and when the browser
// is configured to block site data, and a queue that will not render because it could not
// read a preference is a far worse outcome than one that opens in the default order.

const STORAGE_KEY = 'crm.tickets.sort';

/**
 * The stored order, or '' when there is none or it is no longer a sort we offer.
 *
 * VALIDATED AGAINST THE LIVE LIST, not trusted. A value stored by an older build — or
 * edited by hand — would otherwise be sent to the API, which answers an unknown `sort`
 * with 400 INVALID_QUERY_PARAM and turns a stale preference into a queue that cannot
 * load at all. `sla_resolution_due_at` is exactly that case: a real spelling once,
 * accepted by the server as a deprecated alias, and not in the list any more.
 */
export function readSortPreference(allowed) {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY) ?? '';
    if (!stored) return '';
    return Array.isArray(allowed) && allowed.includes(stored) ? stored : '';
  } catch {
    return '';
  }
}

/** Store an order, or clear it when given '' — which is "use the system default". */
export function writeSortPreference(value) {
  try {
    if (value) window.localStorage.setItem(STORAGE_KEY, value);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // The preference is a convenience, not state the app depends on.
  }
}

/**
 * The sort to actually send, given what the URL asked for and what the agent prefers.
 *
 * THE VIEW IS WHY THIS IS A FUNCTION rather than a `||`. A saved view carries its own
 * order, and the API applies it only when the request names no sort — so sending a
 * preference while a view is open would silently override all 39 imported views and look
 * exactly like view ordering being broken. The preference therefore applies only to the
 * unfiltered queue, which is the screen it was set on.
 *
 * An explicit sort in the URL always wins: a shared or bookmarked link must open the way
 * its author saw it, regardless of who clicks it.
 */
export function effectiveSort({ urlSort, viewId, preference }) {
  if (urlSort) return urlSort;
  if (viewId) return '';
  return preference || '';
}
